// EG mega-menu behaviour: hover intent, click, full keyboard, Escape, outside-close.
// No dependencies. One open panel at a time.
const HOVER_OPEN_MS = 60;
const HOVER_CLOSE_MS = 220;

class EgHeaderMenu extends HTMLElement {
  #openItem = null;
  #timer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('click', this.#onDocumentClick);
  }
  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #trigger(item) { return item.querySelector('[aria-expanded]'); }
  #panel(item) { return item.querySelector('.eg-mega'); }

  #open(item) {
    if (this.#openItem === item) return;
    this.#close();
    this.#trigger(item).setAttribute('aria-expanded', 'true');
    this.#panel(item).hidden = false;
    this.#openItem = item;
  }
  #close() {
    if (!this.#openItem) return;
    this.#trigger(this.#openItem).setAttribute('aria-expanded', 'false');
    this.#panel(this.#openItem).hidden = true;
    this.#openItem = null;
  }

  #onClick = (e) => {
    const trigger = e.target.closest('[aria-expanded]');
    if (!trigger || !this.contains(trigger)) return;
    clearTimeout(this.#timer);
    const item = trigger.closest('[data-eg-menu-item]');
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.#close();
    } else {
      this.#open(item);
    }
  };
  #onPointerOver = (e) => {
    if (e.pointerType === 'touch') return;
    const item = e.target.closest('[data-eg-menu-item]');
    if (!item) return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#open(item), HOVER_OPEN_MS);
  };
  #onPointerOut = (e) => {
    if (e.pointerType === 'touch') return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      if (!this.matches(':hover')) this.#close();
    }, HOVER_CLOSE_MS);
  };
  #onKeydown = (e) => {
    if (e.key === 'Escape' && this.#openItem) {
      const trigger = this.#trigger(this.#openItem);
      this.#close();
      trigger.focus();
    }
  };
  #onFocusOut = (e) => {
    if (!this.contains(e.relatedTarget)) this.#close();
  };
  #onDocumentClick = (e) => {
    if (!this.contains(e.target)) this.#close();
  };
}

if (!customElements.get('eg-header-menu')) {
  customElements.define('eg-header-menu', EgHeaderMenu);
}

// ---------------------------------------------------------------------------
// Deferred mega-menu columns.
//
// The per-column product-type lists cost ~1s of server time (650 product
// objects across 13 collections), so eg-header renders them only when it is
// fetched via the Section Rendering API at /?view=eg-nav&section_id=… (see
// the Liquid comment at the top of sections/eg-header.liquid). This module
// performs that fetch once per page — at idle, or at once on the first
// hover/focus/drawer-open — and drops each column's list into the live DOM.
// Columns are matched by their heading link, so a panel the user already
// has open is patched in place, never rebuilt. With JS unavailable the
// headings (real collection links) remain, which is the no-JS contract.
// ---------------------------------------------------------------------------
const header = document.querySelector('header-component[data-eg-mega-src]');
if (header) {
  let loading = null;

  const fill = (doc) => {
    // Desktop: <ul> per column, matched on the heading href inside the same panel id.
    for (const col of doc.querySelectorAll('.eg-mega__col')) {
      const heading = col.querySelector('.eg-mega__heading');
      const list = col.querySelector('.eg-mega__sublist');
      const panel = col.closest('.eg-mega');
      if (!heading || !list || !panel) continue;
      const href = heading.getAttribute('href');
      // Collection columns match on href; static group headings (no page,
      // e.g. "Tops") match on their text — those carry curated links only,
      // which the inline render already has, so this is a no-op for them.
      const live = href
        ? header.querySelector(`#${CSS.escape(panel.id)} .eg-mega__heading[href="${CSS.escape(href)}"]`)
        : null;
      if (!live) continue;
      live.parentElement.querySelector('.eg-mega__sublist')?.remove();
      live.insertAdjacentElement('afterend', list);
    }
    // Drawer: the sub-group <li>, matched on the sub-heading href.
    for (const group of doc.querySelectorAll('.eg-header-drawer__subgroup')) {
      const href = group.querySelector('.eg-header-drawer__subheading')?.getAttribute('href');
      if (!href) continue;
      const liveLink = header.querySelector(`.eg-header-drawer__sublist > li > a[href="${CSS.escape(href)}"]`);
      liveLink?.parentElement.replaceWith(group);
    }
    header.setAttribute('data-eg-mega-loaded', '');
  };

  const load = () => {
    if (loading) return loading;
    loading = fetch(header.dataset.egMegaSrc, { headers: { Accept: 'text/html' } })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((html) => fill(new DOMParser().parseFromString(html, 'text/html')))
      .catch(() => {
        // Leave the headings-only panel in place; allow a later retry.
        loading = null;
      });
    return loading;
  };

  // Eager on the first sign of intent; otherwise when the browser is idle.
  const intent = ['pointerover', 'focusin', 'touchstart'];
  const onIntent = (e) => {
    if (!e.target.closest('[data-eg-menu-item], [data-eg-drawer-open], .eg-header-drawer')) return;
    intent.forEach((t) => header.removeEventListener(t, onIntent, true));
    load();
  };
  intent.forEach((t) => header.addEventListener(t, onIntent, { capture: true, passive: true }));
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
  idle(() => load(), { timeout: 2500 });
}
